/**
 * Authentication library for the Geaux File portal
 *
 * Wraps AWS Amplify Auth for Cognito integration.
 * Blueprint Task: 2.2.2
 */

import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  signUp,
  signInWithRedirect,
  confirmSignIn,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  updatePassword,
  type SignInInput,
  type SignUpInput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

// Determine redirect URLs based on environment
// Use explicit env vars if set, otherwise derive from window.location.origin
function getRedirectUrls() {
  if (typeof window === 'undefined') {
    return {
      signIn: 'http://localhost:3000/',
      signOut: 'http://localhost:3000/login',
    };
  }
  const origin = window.location.origin;
  return {
    signIn: import.meta.env.VITE_REDIRECT_SIGN_IN || `${origin}/`,
    signOut: import.meta.env.VITE_REDIRECT_SIGN_OUT || `${origin}/login`,
  };
}

const redirectUrls = getRedirectUrls();

// Configure Amplify with Cognito settings
// OAuth is only configured when VITE_COGNITO_DOMAIN is set (portal has it, admin doesn't)
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || '';
const cognitoConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
      ...(cognitoDomain ? {
        loginWith: {
          oauth: {
            domain: cognitoDomain,
            scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [redirectUrls.signIn],
            redirectSignOut: [redirectUrls.signOut],
            responseType: 'code' as const,
          },
        },
      } : {}),
    },
  },
};

Amplify.configure(cognitoConfig);

export interface AuthUser {
  userId: string;
  email: string;
  emailVerified: boolean;
  name?: string;
  role?: 'customer' | 'admin' | 'super_admin' | 'operator' | 'owner' | 'viewer';
  oauthProvider?: string; // 'Google' | 'Apple' | 'Microsoft' | undefined
}

/**
 * Role mapping from Cognito UPPERCASE to portal lowercase
 * Backend/Cognito uses: OWNER, ADMIN, OPERATOR, VIEWER, CUSTOMER
 * Portal UI uses: owner, admin, operator, viewer, customer
 */
const COGNITO_TO_PORTAL_ROLES: Record<string, AuthUser['role']> = {
  OWNER: 'owner',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  OPERATOR: 'operator',
  VIEWER: 'viewer',
  CUSTOMER: 'customer',
};

/**
 * Normalizes Cognito UPPERCASE role to lowercase portal role
 * @param cognitoRole - Role from Cognito JWT (UPPERCASE)
 * @returns Lowercase role for UI display, or undefined if invalid
 */
export function normalizeRole(cognitoRole: string | undefined): AuthUser['role'] | undefined {
  if (!cognitoRole) return undefined;
  const upperRole = cognitoRole.toUpperCase();
  return COGNITO_TO_PORTAL_ROLES[upperRole] ?? (cognitoRole.toLowerCase() as AuthUser['role']);
}

export interface AuthError {
  code: string;
  message: string;
}

/**
 * Sign in with email and password
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  const input: SignInInput = {
    username: email,
    password,
  };

  const result = await signIn(input);

  if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
    throw { code: 'UserNotConfirmedException', message: 'Please verify your email first' };
  }

  if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    throw { code: 'NewPasswordRequired', message: 'Please set a new password' };
  }

  return getCurrentAuthUser();
}

/**
 * Complete the NEW_PASSWORD_REQUIRED challenge after first login with temp password.
 * Must be called while the signIn challenge is still active (same session).
 */
export async function completeNewPassword(newPassword: string): Promise<AuthUser> {
  await confirmSignIn({ challengeResponse: newPassword });
  return getCurrentAuthUser();
}

/**
 * Sign out the current user
 */
export async function logout(): Promise<void> {
  await signOut();
}

/**
 * Register a new user
 */
export async function register(
  email: string,
  password: string,
  firstName: string,
  lastName: string
): Promise<{ isSignUpComplete: boolean; userId?: string }> {
  const input: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        name: [firstName, lastName].filter(Boolean).join(' '),
        given_name: firstName,
        family_name: lastName,
      },
    },
  };

  const result = await signUp(input);
  return {
    isSignUpComplete: result.isSignUpComplete,
    userId: result.userId,
  };
}

/**
 * Confirm registration with verification code
 */
export async function confirmRegistration(email: string, code: string): Promise<void> {
  await confirmSignUp({
    username: email,
    confirmationCode: code,
  });
}

/**
 * Initiate password reset
 */
export async function forgotPassword(email: string): Promise<void> {
  await resetPassword({ username: email });
}

/**
 * Complete password reset with code
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

/**
 * Get the current authenticated user
 */
export async function getCurrentAuthUser(): Promise<AuthUser> {
  const user = await getCurrentUser();
  const session = await fetchAuthSession();

  const idToken = session.tokens?.idToken;
  const payload = idToken?.payload;

  // Extract role from Cognito groups or custom:role claim
  // Pre Token Generation trigger sets custom:role, but it may not be in token
  // Fallback to cognito:groups which is always present
  // Cognito uses UPPERCASE (OWNER, ADMIN, OPERATOR, VIEWER)
  // Portal UI uses lowercase (owner, admin, operator, viewer)
  const groups = payload?.['cognito:groups'] as string[] | undefined;
  const roleValue = (
    payload?.['custom:role'] as string | undefined ??
    groups?.[0] // Fallback to first group if custom:role missing
  );

  const role = normalizeRole(roleValue);

  // Extract OAuth provider from ID token 'identities' claim
  // Cognito populates this for federated users (Google, Apple, Microsoft)
  const identities = payload?.identities as { providerName?: string }[] | undefined;
  const oauthProvider = identities?.[0]?.providerName;

  return {
    userId: user.userId,
    email: (payload?.email as string) || user.username,
    emailVerified: (payload?.email_verified as boolean) || false,
    name: payload?.name as string | undefined,
    role,
    oauthProvider,
  };
}

/**
 * Get the current session tokens
 */
export async function getSession() {
  return fetchAuthSession();
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the access token for API calls
 */
export async function getAccessToken(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  return session.tokens?.accessToken?.toString();
}

/**
 * Get the ID token for API calls
 */
export async function getIdToken(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

/**
 * Fetch current user's Cognito attributes
 */
export async function getUserAttributes(): Promise<Record<string, string>> {
  const attrs = await fetchUserAttributes();
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Update user profile attributes (name, phone)
 */
export async function updateProfile(attributes: {
  name?: string;
  phone_number?: string;
}): Promise<void> {
  const userAttributes: Record<string, string> = {};
  if (attributes.name !== undefined) {
    userAttributes.name = attributes.name;
    const parts = attributes.name.trim().split(/\s+/);
    userAttributes.given_name = parts[0] || '';
    userAttributes.family_name = parts.slice(1).join(' ') || '';
  }
  if (attributes.phone_number !== undefined) userAttributes.phone_number = attributes.phone_number;

  await updateUserAttributes({ userAttributes });
}

/**
 * Change user's password
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  await updatePassword({ oldPassword, newPassword });
}

// ==========================================================================
// OAuth / Social Login
// ==========================================================================

export type OAuthProvider = 'Google' | 'Apple' | 'Microsoft';

/**
 *
 */
export async function signInWithOAuth(provider: OAuthProvider): Promise<void> {
  // Clear any stale Cognito session — Amplify v6 throws
  // UserAlreadyAuthenticatedException if residual tokens exist in localStorage.
  try { await signOut(); } catch { /* no session to clear */ }

  // signInWithRedirect triggers window.location.assign — browser navigates away.
  // Return the promise so callers can .catch() config/storage errors,
  // but the promise will never resolve on success (page unloads).
  if (provider === 'Google' || provider === 'Apple') {
    return signInWithRedirect({ provider });
  } else {
    return signInWithRedirect({ provider: { custom: 'Microsoft' } });
  }
}

/**
 *
 */
export function onOAuthSignIn(
  callback: (event: 'success' | 'failure', error?: unknown) => void
): () => void {
  return Hub.listen('auth', (data) => {
    if (data.payload.event === 'signInWithRedirect') {
      callback('success');
    } else if (data.payload.event === 'signInWithRedirect_failure') {
      callback('failure', data.payload.data);
    }
  });
}
